<template>
  <div class="review-overlay" role="presentation" @click.self="$emit('cancel')">
    <section
      ref="dialog"
      tabindex="-1"
      @keydown.esc.prevent.stop="emit('cancel')"
      @keydown.tab="trapFocus"
      class="review-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="right-to-work-review-title"
    >
      <header class="review-header">
        <div>
          <p class="review-eyebrow">AI document reading</p>
          <h2 id="right-to-work-review-title">Review Right to Work details</h2>
          <p class="review-source">{{ result?.source?.filename || 'Attached PDF' }}</p>
        </div>
        <span class="confidence-badge" :class="confidenceClass">
          {{ confidenceLabel }} confidence
        </span>
      </header>

      <div class="review-workspace">
        <section class="review-document" aria-label="Source PDF">
          <div v-if="previewLoading" class="preview-state" role="status">Loading document…</div>
          <div v-else-if="previewError" class="preview-state" role="alert">
            <p>{{ previewError }}</p>
            <button type="button" class="secondary-button" @click="loadPreview">Retry</button>
          </div>
          <PdfDocumentPreview v-else-if="previewUrl" :src="previewUrl" />
        </section>
        <section class="review-fields" aria-label="Extracted Right to Work details">
          <ul v-if="warnings.length" class="warning-list">
            <li v-for="warning in warnings" :key="warning">{{ warning }}</li>
          </ul>
          <CatalogItemForm v-model="editableValues" :template="reviewTemplate" fixed-date-pickers />
          <details v-if="evidence.length" class="evidence-block">
            <summary>Extracted text</summary>
            <ul><li v-for="entry in evidence" :key="entry">“{{ entry }}”</li></ul>
          </details>
        </section>
      </div>

      <footer class="review-actions">
        <button type="button" class="secondary-button" @click="$emit('cancel')">Cancel</button>
        <button type="button" class="primary-button" @click="applyChanges">
          Apply Right to Work details
        </button>
      </footer>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import PdfDocumentPreview from '@/components/common/PdfDocumentPreview.vue';
import { useMediaStore } from '@/stores/media';
import CatalogItemForm from '@/components/catalog/CatalogItemForm.vue';
import { catalogTemplates } from '@/constants/catalogTemplates';
import { isMissingValidityDateWarning } from '@/utils/rightToWorkExtractionWarnings';

const props = defineProps({
  result: {
    type: Object,
    required: true,
  },
  currentValues: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['apply', 'cancel']);
const mediaStore = useMediaStore();
const dialog = ref(null);
const previewUrl = ref('');
const previewLoading = ref(false);
const previewError = ref('');
let previewToken = 0;
let previousFocus = null;

const loadPreview = async () => {
  const token = ++previewToken;
  previewUrl.value = '';
  previewError.value = '';
  previewLoading.value = true;
  try {
    const fileId = props.result?.source?.fileId;
    if (!fileId) throw new Error('The source PDF is unavailable.');
    const signed = await mediaStore.getSignedUrl(fileId, { disposition: 'view' });
    if (token !== previewToken) return;
    if (!signed?.url) throw new Error('The source PDF is unavailable.');
    previewUrl.value = signed.url;
  } catch {
    if (token === previewToken) previewError.value = 'Unable to load the source PDF.';
  } finally {
    if (token === previewToken) previewLoading.value = false;
  }
};

const trapFocus = (event) => {
  const elements = [...(dialog.value?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], summary, [tabindex="0"]') || [])]
    .filter((element) => element.getClientRects().length);
  const first = elements[0];
  const last = elements.at(-1);
  if (!first) { event.preventDefault(); return; }
  if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.value)) {
    event.preventDefault(); last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault(); first.focus();
  }
};

watch(() => props.result?.source?.fileId, loadPreview, { immediate: true });
onMounted(async () => {
  previousFocus = document.activeElement;
  await nextTick();
  dialog.value?.focus();
});
onBeforeUnmount(() => {
  previewToken++;
  if (previousFocus?.isConnected) previousFocus.focus();
});

const REVIEW_FIELD_KEYS = [
  'rightToWorkListType',
  'rightToWorkCheckMethod',
  'rightToWorkCheckedBy',
  'rightToWorkCheckDate',
  'rightToWorkFollowUpBasis',
  'rightToWorkPvnValidUntil',
  'rightToWorkDocumentDate',
  'rightToWorkExpiryDate',
  'rightToWorkDocumentReference',
  'rightToWorkOutcome',
];

const reviewTemplate = {
  key: 'suppliers',
  rows: (catalogTemplates.suppliers?.rows || [])
    .filter((row) => (
      row.section === 'right-to-work-uk'
      && row.subsection !== 'status'
    )),
};

const editableValues = ref({});
const extraction = computed(() => props.result?.extraction || {});
const warnings = computed(() => {
  const entries = Array.isArray(extraction.value?.warnings) ? extraction.value.warnings : [];
  return entries.filter((entry) => !(
    editableValues.value.rightToWorkListType === 'list_a'
    && editableValues.value.rightToWorkCheckMethod !== 'home_office_employer_checking_service'
    && isMissingValidityDateWarning(entry)
  ));
});
const evidence = computed(() => Array.isArray(extraction.value?.evidence)
  ? extraction.value.evidence
  : []);
const confidence = computed(() => Number(extraction.value?.confidence || 0));
const confidenceLabel = computed(() => `${Math.round(confidence.value * 100)}%`);
const confidenceClass = computed(() => {
  if (confidence.value >= 0.85) return 'high';
  if (confidence.value >= 0.65) return 'medium';
  return 'low';
});

const buildEditableValues = () => {
  const detected = extraction.value?.fields || {};
  const next = {};

  REVIEW_FIELD_KEYS.forEach((key) => {
    // Missing facts in a new PDF must not inherit dates/references from the previous document.
    next[key] = detected[key] ?? '';
  });

  next.rightToWorkCheckedBy = detected.rightToWorkCheckedBy || '';

  if (next.rightToWorkListType === 'list_b') {
    next.rightToWorkFollowUpBasis ||= 'permission_expiry';
  } else {
    next.rightToWorkExpiryDate = '';
  }

  return next;
};

watch(
  () => props.result,
  () => {
    editableValues.value = buildEditableValues();
  },
  { immediate: true },
);

const applyChanges = () => {
  const patch = Object.fromEntries(
    REVIEW_FIELD_KEYS.map((key) => [key, editableValues.value?.[key] ?? '']),
  );

  if (patch.rightToWorkListType !== 'list_b') {
    patch.rightToWorkExpiryDate = '';
  }

  emit('apply', patch);
};
</script>

<style scoped>
.review-overlay { position: fixed; inset: 0; z-index: 1200; display: grid; place-items: center; padding: 20px; background: rgba(12, 14, 22, 0.45); }
.review-dialog { display: flex; flex-direction: column; width: min(1320px, 100%); height: min(920px, calc(100dvh - 40px)); min-height: 0; overflow: hidden; border: 1px solid var(--border-color, #dfe3ea); border-radius: 10px; background: var(--noc-background, #fff); color: var(--text-color, #414552); box-shadow: 0 20px 60px #0c0e1633; outline: none; }
.review-header { display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; gap: 16px; padding: 16px 20px; border-bottom: 1px solid var(--border-color, #dfe3ea); }
.review-header > div { min-width: 0; }
.review-eyebrow, .review-source { margin: 0; color: var(--text-color-secondary, #656b7b); font-size: 0.76rem; }
.review-eyebrow { margin-bottom: 4px; }
.review-source { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.review-header h2 { margin: 0 0 4px; font-size: 1.1rem; }
.confidence-badge { flex: 0 0 auto; border-radius: 5px; padding: 5px 8px; background: #eef1f5; color: #5d6472; font-size: 0.75rem; font-weight: 600; }
.confidence-badge.high { background: #e8f6ef; color: #197348; }
.confidence-badge.medium { background: #fff5dc; color: #8a5b00; }
.confidence-badge.low { background: #fdecec; color: #b32727; }
.review-workspace { display: grid; grid-template-columns: minmax(0, 1fr) minmax(360px, 440px); flex: 1; min-height: 0; }
.review-document { min-width: 0; min-height: 0; border-right: 1px solid var(--border-color, #dfe3ea); }
.review-fields { min-width: 0; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 20px; }
.review-fields :deep(.drp-dropdown) { margin-top: 0; }
.review-fields :deep(.collapsible-content) { padding-left: 0; }
.review-fields :deep(.right-to-work-subsection) { min-width: 0; }
.warning-list { margin: 0 0 16px; padding: 10px 10px 10px 28px; border: 1px solid #efd8a1; border-radius: 6px; background: #fff9ea; color: #705113; font-size: 0.82rem; }
.evidence-block { margin-top: 20px; font-size: 0.8rem; color: var(--text-color-secondary, #656b7b); line-height: 1.5; }
.evidence-block summary { cursor: pointer; font-weight: 600; }
.evidence-block ul { padding-left: 20px; }
.preview-state { padding: 32px; text-align: center; font-size: 0.86rem; color: var(--text-color-secondary, #656b7b); }
.review-actions { display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; padding: 12px 20px; border-top: 1px solid var(--border-color, #dfe3ea); }
.secondary-button, .primary-button { min-height: 38px; border-radius: 6px; padding: 0 14px; font: inherit; font-size: 0.84rem; font-weight: 600; cursor: pointer; touch-action: manipulation; transition: background-color 0.15s; }
.secondary-button { border: 1px solid var(--border-color, #dfe3ea); background: var(--noc-background, #fff); color: inherit; }
.primary-button { border: 1px solid var(--noc-blue, #0a61c9); background: var(--noc-blue, #0a61c9); color: #fff; }
.secondary-button:hover { background: var(--noc-background-secondary, #f7f8fa); }
.primary-button:hover { background: #0956b2; }
button:focus-visible { outline: 2px solid var(--noc-blue, #0a61c9); outline-offset: 2px; }
@media (max-width: 1000px) { .review-workspace { grid-template-columns: minmax(0, 1fr) minmax(320px, 42%); } .review-fields { padding: 14px; } }
@media (max-width: 700px) {
  .review-overlay { padding: 8px; }
  .review-dialog { height: calc(100dvh - 16px); }
  .review-header { padding: 12px; }
  .review-header h2 { font-size: 0.95rem; }
  .confidence-badge { font-size: 0.65rem; }
  .review-workspace { grid-template-columns: 1fr; grid-template-rows: minmax(160px, 42%) minmax(0, 1fr); }
  .review-document { border-right: 0; border-bottom: 1px solid var(--border-color, #dfe3ea); }
  .review-actions { padding: 10px 12px; }
  .secondary-button, .primary-button { min-height: 42px; }
}
</style>
